/* eslint-disable complexity */
import React from 'react';
import {
    fontSizes, mediaQueries, radii, space, buttons
} from 'style/config';
import {
    Flex, Box, Text, Button
} from 'components/ui';
import uiUtils from 'utils/UI';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import ProductQuicklook from 'components/Product/ProductQuicklook/ProductQuicklook';
import StarRating from 'components/StarRating/StarRating';
import ReviewCount from 'components/Product/ReviewCount/ReviewCount';
import Location from 'utils/Location';
import localeUtils from 'utils/LanguageLocale';
import anaUtils from 'analytics/utils';
import anaConsts from 'analytics/constants';
import urlUtils from 'utils/Url';
import helpersUtils from 'utils/Helpers';
import stringUtils from 'utils/String';
import processEvent from 'analytics/processEvent';
import pdpCompareUtils from 'utils/pdpCompare';

const { deferTaskExecution } = helpersUtils;
const { capitalize } = stringUtils;

import ProductCompareToolItemEN from 'components/ProductPage/ProductCompareTool/ProductCompareToolItem/locales/ProductCompareToolItem_en_US.js';
import ProductCompareToolItemFR from 'components/ProductPage/ProductCompareTool/ProductCompareToolItem/locales/ProductCompareToolItem_fr_CA.js';

// Helper function to get localized text
const getText = localeUtils.getLocalisedDataFromResource(ProductCompareToolItemEN, ProductCompareToolItemFR);

// Analytics helper functions
const getComponentTitle = () => anaConsts.PAGE_TYPES.SIMILAR_PRODUCTS;

const trackProductClickAnalytics = (sku, index, podId, isCarousel) => {
    const componentTitle = getComponentTitle();
    const clickedProduct = Number(index) + 1;
    const internalCampaign = `${componentTitle?.toLowerCase()}:product`;

    const nextPageData = {
        recInfo: {
            isExternalRec: !!podId,
            componentTitle,
            podId
        },
        linkData: `${internalCampaign} ${clickedProduct}`,
        internalCampaign,
        ...(isCarousel && { events: [anaConsts.Event.EVENT_269] })
    };

    anaUtils.setNextPageData(nextPageData);

    // Dispatches the Select Item Event
    processEvent.process(anaConsts.SELECT_ITEM_EVENT, {
        data: {
            listName: componentTitle?.toLowerCase() || '',
            listId: '',
            productId: sku?.productId?.toLowerCase() || '',
            price: sku?.listPrice || '',
            brandName: sku?.brandName || '',
            productName: sku?.productName || '',
            productIndex: index || 0
        }
    });
};

const handleProductClick = (e, sku, index, podId, isCarousel) => {
    e.preventDefault();

    deferTaskExecution(() => {
        trackProductClickAnalytics(sku, index, podId, isCarousel);
    });

    Location.navigateTo(e, sku.targetUrl);
};

// Cell rendering functions
export const renderTopCell = ({
    sku, index, podId, isCarousel, isSkeleton
}) => {
    const { SKELETON_ANIMATION } = uiUtils;
    const componentTitle = getComponentTitle();
    const targetUrl = urlUtils.getLink(sku?.targetUrl);

    return (
        <Box
            key={`top-${index}`}
            backgroundColor={index === 0 && !isSkeleton && 'nearWhite'}
            borderLeftWidth={index > 0 && 1}
            borderColor={index > 0 && 'divider'}
            paddingTop={4}
            paddingLeft={4}
            paddingRight={4}
            {...(!isSkeleton &&
                sku?.strategyId && {
                'data-cnstrc-item': 'recommendation',
                'data-cnstrc-item-id': sku?.productId,
                'data-cnstrc-item-name': sku?.productName,
                'data-cnstrc-item-variation-id': sku?.variationId,
                'data-cnstrc-strategy-id': sku?.strategyId,
                'data-cnstrc-item-price': sku?.salePrice || sku?.listPrice
            })}
        >
            <div css={styles.cellTop}>
                <Box
                    position='relative'
                    marginBottom={4}
                    {...(!isSkeleton &&
                        index > 0 && {
                        ['aria-label']: `${sku.brandName} ${sku.productName}`,
                        href: targetUrl,
                        onClick: e => handleProductClick(e, sku, index, podId, isCarousel),
                        css: styles.ql
                    })}
                >
                    {isSkeleton ? (
                        <div css={[skeletonStyles.image, SKELETON_ANIMATION]} />
                    ) : (
                        <>
                            {sku?.skuId && (
                                <ProductImage
                                    id={sku.skuId}
                                    skuImages={sku.skuImages}
                                    size={160}
                                />
                            )}
                            {index > 0 && (
                                <div className='ProductTableItem-ql'>
                                    <ProductQuicklook
                                        isShown={true}
                                        showQuickLookOnMobile={true}
                                        rootContainerName={componentTitle}
                                        productStringContainerName={componentTitle}
                                        sku={sku}
                                        podId={podId}
                                        isCarousel={isCarousel}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Box>
                <Text
                    display='block'
                    fontSize={['xs', 'sm']}
                    marginBottom='.125em'
                    fontWeight='bold'
                    numberOfLines={1}
                    css={isSkeleton && [skeletonStyles.text, SKELETON_ANIMATION]}
                    children={isSkeleton ? '\u00A0' : sku?.brandName}
                />
                <Text
                    display='block'
                    numberOfLines={2}
                    css={isSkeleton && [skeletonStyles.text, SKELETON_ANIMATION]}
                    children={isSkeleton ? '\u00A0' : sku?.productName}
                />
                {isSkeleton ? (
                    <>
                        <div css={[skeletonStyles.price, SKELETON_ANIMATION]}>&nbsp;</div>
                        <div css={[skeletonStyles.button, SKELETON_ANIMATION]} />
                    </>
                ) : (
                    index > 0 && (
                        <Button
                            block={true}
                            size='sm'
                            marginTop='auto'
                            variant='secondary'
                            onClick={e => handleProductClick(e, sku, index, podId, isCarousel)}
                            href={targetUrl}
                            children={getText('seeDetails')}
                        />
                    )
                )}
            </div>
        </Box>
    );
};

export const renderPriceCell = ({ sku, index }) => {
    const valuePrice = sku?.valuePrice
        ? sku.valuePrice
            .toLowerCase()
            .replace(getText('value'), '')
            .replace(/[\(\)]+/g, '')
            .trim()
        : null;

    return (
        <Box
            key={`price-${index}`}
            backgroundColor={index === 0 && 'nearWhite'}
            borderLeftWidth={index > 0 && 1}
            borderColor={index > 0 && 'divider'}
            borderTopColor='transparent'
            paddingLeft={4}
            paddingRight={4}
        >
            <div css={[styles.cell, styles.cellPrice]}>
                <>
                    <Text fontWeight='bold'>
                        {sku.salePrice && (
                            <>
                                <Text
                                    color='red'
                                    children={sku.salePrice}
                                />{' '}
                            </>
                        )}
                        <span
                            css={sku.salePrice && styles.priceList}
                            children={sku.listPrice}
                        />
                    </Text>
                    {valuePrice && <Text is='p'>{`(${valuePrice} ${getText('value')})`}</Text>}
                </>
            </div>
        </Box>
    );
};

export const renderRatingCell = ({ sku, index }) => {
    const hasSentiments = sku?.sentiments && Array.isArray(sku.sentiments) && sku.sentiments.length > 0;
    const hasReviews = sku?.productReviewCount > 0;

    const formattedSentiments = hasSentiments ? sku.sentiments.map(s => `${capitalize(s.sentiment)} (${s.count})`).join(', ') : '';

    return (
        <Box
            key={`rating-${index}`}
            backgroundColor={index === 0 && 'nearWhite'}
            borderLeftWidth={index > 0 && 1}
            borderColor={index > 0 && 'divider'}
            borderTopWidth={1}
            borderTopColor='divider'
            paddingLeft={4}
            paddingRight={4}
        >
            <div
                css={{
                    ...styles.cell,
                    ...(!hasReviews && {
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    })
                }}
            >
                {sku && (
                    <>
                        {!hasReviews && <Text aria-label='No reviews'>-</Text>}
                        {hasReviews && !hasSentiments && (
                            <Flex
                                alignItems='center'
                                lineHeight='none'
                            >
                                {sku.starRatings ? (
                                    <Text
                                        children={sku.starRatings?.toFixed(1)}
                                        css={styles.reviewRating}
                                    />
                                ) : null}
                                <StarRating
                                    size={12}
                                    rating={sku.starRatings}
                                />
                                <ReviewCount
                                    css={styles.reviewCount}
                                    productReviewCount={sku.productReviewCount}
                                    showParentheses
                                />
                            </Flex>
                        )}
                        {hasReviews && hasSentiments && (
                            <>
                                <Flex
                                    alignItems='center'
                                    lineHeight='none'
                                    marginBottom={2}
                                >
                                    {sku.starRatings ? (
                                        <Text
                                            children={sku.starRatings?.toFixed(1)}
                                            css={styles.reviewRating}
                                        />
                                    ) : null}
                                    <StarRating
                                        size={12}
                                        rating={sku.starRatings}
                                    />
                                    <ReviewCount
                                        css={styles.reviewCount}
                                        productReviewCount={sku.productReviewCount}
                                        showParentheses
                                    />
                                </Flex>
                                <Box>
                                    <Text>
                                        <span css={styles.highlyRatedLabel}>{getText('highlyRated', [])}:</span> {formattedSentiments}
                                    </Text>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </div>
        </Box>
    );
};

export const renderAttributeCell = ({ sku, index, attribute, attrIndex }) => {
    if (!attribute || !sku) {
        return { cell: null, isCellEmpty: true };
    }

    let refinementName;
    let refinementValues;

    if (typeof attribute === 'string') {
        const skuFacet = sku?.facets?.find(facet => facet.name === attribute);
        const masterListRefinement = skuFacet?.values?.length ? pdpCompareUtils.findRefinementInMasterList(skuFacet.name) : null;

        if (masterListRefinement) {
            refinementName = masterListRefinement.value || null;
            const mappedValues = pdpCompareUtils.mapFacetValues(skuFacet.values, masterListRefinement);
            refinementValues = mappedValues.length > 0 ? mappedValues : ['-'];
        } else {
            refinementValues = ['-'];
        }
    }

    if (attribute && typeof attribute === 'object' && attribute !== null && !Array.isArray(attribute)) {
        const attributeKey = Object.keys(attribute)[0];
        const attributeValues = Object.values(attribute)[0];

        if (Array.isArray(attributeValues) && attributeValues.length > 0) {
            const facetMap = new Map(sku?.facets?.map(f => [f.name, f]) || []);
            refinementValues = [];

            const directFacet = facetMap.get(attributeKey);

            if (directFacet?.values?.length > 0) {
                const masterListRefinement = pdpCompareUtils.findRefinementInMasterList(directFacet.name);

                if (masterListRefinement) {
                    const values = pdpCompareUtils.mapFacetValues(directFacet.values, masterListRefinement);

                    if (values.length > 0) {
                        refinementValues.push(...values);
                    }
                }
            } else {
                attributeValues.forEach(attValue => {
                    const skuFacet = facetMap.get(attValue);

                    if (skuFacet?.values?.length > 0) {
                        const masterListRefinement = pdpCompareUtils.findRefinementInMasterList(skuFacet.name);

                        if (masterListRefinement) {
                            const values = pdpCompareUtils.mapFacetValues(skuFacet.values, masterListRefinement);

                            if (values.length > 0) {
                                refinementValues.push(...values);
                            }
                        }
                    }
                });
            }

            if (refinementValues.length > 0) {
                refinementName = getText(attributeKey) || attributeKey;
                const refinementValuesText = refinementValues.join(', ');

                if (attributeKey === 'targetedConcerns') {
                    refinementValues = [refinementValuesText];
                } else {
                    const refinementText =
                        attributeValues.includes('hairType') && attributeValues.includes('hairTexture')
                            ? getText('hairValues')
                            : getText('skinValues');
                    refinementValues = localeUtils.isFrench()
                        ? [`${refinementText} ${refinementValuesText}`]
                        : [`${refinementValuesText} ${refinementText}`];
                }
            } else {
                refinementValues = ['-'];
            }
        }
    }

    if (!refinementValues?.length) {
        return { cell: null, isCellEmpty: true };
    }

    const showDash = refinementValues.length === 1 && refinementValues[0] === '-';

    const cell = (
        <Box
            key={`attr-${attrIndex}-${index}`}
            backgroundColor={index === 0 && 'nearWhite'}
            borderLeftWidth={index > 0 && 1}
            borderColor={index > 0 && 'divider'}
            borderTopWidth={1}
            borderTopColor='divider'
            paddingLeft={4}
            paddingRight={4}
        >
            <div
                css={{
                    ...styles.cell,
                    ...(showDash
                        ? {
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                        }
                        : {})
                }}
            >
                {showDash ? (
                    <Text aria-label='Not applicable'>-</Text>
                ) : (
                    <div>
                        {refinementName && <Text fontWeight='bold'>{refinementName}: </Text>}
                        <Text>{refinementValues.join(', ')}</Text>
                    </div>
                )}
            </div>
        </Box>
    );

    return { cell, isCellEmpty: showDash };
};

const skeletonStyles = {
    image: {
        paddingBottom: '100%',
        borderRadius: radii[2]
    },
    text: {
        borderRadius: radii.full
    },
    price: {
        display: 'block',
        width: '3em',
        borderRadius: radii.full,
        marginBottom: space[2]
    },
    button: {
        borderRadius: radii.full,
        height: buttons.HEIGHT_SM,
        width: '100%',
        marginTop: 'auto'
    }
};

const styles = {
    cellTop: {
        display: 'flex',
        flexDirection: 'column',
        height: 268,
        [mediaQueries.sm]: {
            height: 274
        }
    },
    cell: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        paddingTop: space[3],
        paddingBottom: space[3]
    },
    ql: {
        '& .ProductTableItem-ql': {
            opacity: 0,
            transition: 'opacity .2s',
            [mediaQueries.xsMax]: {
                display: 'none'
            }
        },
        '.no-touch &:hover, :focus, :focus-within': {
            '& .ProductTableItem-ql': {
                opacity: 1
            }
        }
    },
    priceList: {
        fontWeight: 'var(--font-weight-normal)',
        textDecoration: 'line-through'
    },
    reviewCount: {
        fontSize: fontSizes.sm,
        marginLeft: '.375em',
        position: 'relative',
        top: '.0625em'
    },
    reviewRating: {
        fontSize: fontSizes.sm,
        fontWeight: 'bold',
        marginRight: '.375em',
        position: 'relative',
        top: '.0625em'
    },
    highlyRatedLabel: {
        fontWeight: 'bold'
    }
};
